/* Creating a spot the collection does not hold: where hero's range comes from, and the spot
   a finished draft turns into. */

import { FLAT, combosOf, r1 } from "./hands";
import { continuingRange, presenceOf, rangeField, typedRange } from "./notation";
import { HERO_ALREADY_ACTED, actionLabel, oppCount } from "./rules";
import { seatName } from "./seats";
import { seatLabelsFor, spotKey, type Spot, type SpotIndex, type SpotPlace } from "./spot";

/* How a new spot gets hero's range. "full" and "typed" state it outright; "copy" borrows
   another chart's hand list; "cont" derives it from the chart holding hero's previous
   decision. `from` is "full", "typed", "cont:<key>" or the key of the chart copied. */
export const RANGE_SOURCES = ["full", "typed", "copy", "cont"] as const;
export type RangeKind = (typeof RANGE_SOURCES)[number];

export interface RangeSpec {
  from?: string | null | undefined;
  action: string;
  rangeNote?: string | undefined;
  rangeFloor?: number | undefined;
}

export interface SpotSpec extends RangeSpec, Omit<SpotPlace, "action"> {
  seatLabels?: string[] | undefined;
}

export const rangeKind = (c: Pick<RangeSpec, "from">): RangeKind =>
  !c.from || c.from === "full"
    ? "full"
    : c.from === "typed"
      ? "typed"
      : c.from.startsWith("cont:")
        ? "cont"
        : "copy";

/** The key of the chart a draft borrows from, or null when it borrows from none. */
export function sourceKey(c: Pick<RangeSpec, "from">): string | null {
  const kind = rangeKind(c);
  if (kind === "cont") return (c.from ?? "").slice(5);
  return kind === "copy" ? (c.from ?? null) : null;
}

export type RangeResult =
  | { error: string }
  | { live: Map<string, number>; exact: boolean; warn?: string[]; src?: Spot; error?: undefined };

/** The presence map a spec asks for, or null if its source has gone missing. */
export function rangeForSpec(index: SpotIndex, spec: RangeSpec): RangeResult | null {
  const kind = rangeKind(spec);
  if (kind === "full") return { live: new Map(FLAT.map((h) => [h, 1])), exact: true };
  if (kind === "typed") {
    try {
      const r = typedRange(spec.rangeNote);
      if (!r.live.size) return { error: "Type at least one hand for hero's range." };
      return { live: r.live, exact: true, warn: r.warn };
    } catch (e) {
      return { error: e instanceof Error ? e.message : String(e) };
    }
  }
  const src = index.get(sourceKey(spec) ?? "");
  if (!src) return null;
  if (kind === "cont") {
    const step = HERO_ALREADY_ACTED[spec.action];
    const live = continuingRange(src, spec.rangeFloor ?? 0, step?.via);
    if (!live.size)
      return {
        error: `Nothing ${step ? step.does : "continues"} in that chart, so hero has no range here.`,
      };
    return { live, exact: true, src };
  }
  return { live: presenceOf(src), exact: false, src };
}

/**
 * A spot the collection never held. Hero's range is whatever the spec asks for; the chart it
 * is copied from, if any, only supplies a starting shape. Null when that chart has gone.
 */
export function spotFromSpec(index: SpotIndex, spec: SpotSpec): Spot | null {
  const range = rangeForSpec(index, spec);
  if (!range) return null;
  if (range.error !== undefined) throw new Error(range.error);

  const seats = [...spec.seats];
  const place: SpotPlace = {
    stakes: spec.stakes,
    gameType: spec.gameType,
    format: spec.format,
    stack: spec.stack,
    openSize: spec.openSize,
    hero: spec.hero,
    action: spec.action,
    seats,
    ...(spec.branch ? { branch: spec.branch } : {}),
    // A variation is a spot of its own, so the profile is part of the place it is written in
    // and goes into the key with the rest of it.
    ...(spec.profile ? { profile: spec.profile } : {}),
  };
  const shape = range.src
    ? structuredClone({
        actions: range.src.actions,
        totalCombos: range.src.totalCombos,
        stats: range.src.stats,
        cells: range.src.cells,
      })
    : { actions: [], totalCombos: 0, stats: {}, cells: {} };
  return {
    ...shape,
    ...place,
    key: spotKey(place),
    exact: range.exact,
    baseInRange: [...range.live.keys()],
    range: rangeField(range.live),
    // The action names its opponents; a created spot should label them the way the stored
    // ones do.
    seatLabels:
      spec.seatLabels && spec.seatLabels.length === seats.length
        ? [...spec.seatLabels]
        : seatLabelsFor(spec.action, seats),
    copiedFrom: range.src?.id ?? null,
  };
}

/** The part of a draft that decides which charts hero's range can carry forward from. */
export type DraftPlace = Pick<SpotPlace, "gameType" | "format" | "stack" | "openSize"> & {
  stakes?: string | null;
  hero: string;
  action: string;
  seats: readonly (string | null)[];
};

/**
 * Charts that could hold hero's previous decision in this spot. Hero's range only carries
 * forward from the same seat at the same table for the same money: another stack or another
 * open size is a different game and opens a different range, so those charts are not
 * offered at all.
 */
export function priorSources(spots: readonly Spot[], c: DraftPlace): Spot[] {
  const step = HERO_ALREADY_ACTED[c.action];
  if (!step) return [];
  const prior = step.prior;
  return spots.filter(
    (s) =>
      s.action === prior &&
      (!c.stakes || s.stakes === c.stakes) &&
      s.gameType === c.gameType &&
      s.format === c.format &&
      s.stack === c.stack &&
      s.openSize === c.openSize &&
      s.hero === c.hero &&
      // RFI names nobody, so there is no opponent seat to agree on.
      (oppCount(prior) === 0 || s.seats[0] === c.seats[0]),
  );
}

/** The one to start on: hero's prior decision in this spot. */
export const priorChart = (spots: readonly Spot[], c: DraftPlace): Spot | null =>
  priorSources(spots, c)[0] ?? null;

/** Why the continuing-range option is not on offer, or "" when it is. */
export function contFault(spots: readonly Spot[], c: DraftPlace): string {
  const step = HERO_ALREADY_ACTED[c.action];
  if (!step)
    return `Hero has not acted yet in a ${actionLabel(c.action)} spot, so there is nothing to continue from.`;
  if (priorSources(spots, c).length) return "";
  const who = oppCount(step.prior) === 0 ? "" : ` vs. ${seatName(c.seats[0] ?? "")}`;
  return (
    `No ${step.prior} chart for ${seatName(c.hero)}${who} at ${c.stack || "this stack"} ` +
    `${c.openSize || ""}. Type hero's range instead.`
  );
}

/** The starting range a draft asks for, as a line the user can check. */
export function rangeSummary(index: SpotIndex, c: RangeSpec): string {
  const r = rangeForSpec(index, c);
  if (!r) return "That chart is no longer in the collection.";
  if (r.error !== undefined) return r.error;
  let hands = 0;
  let cbo = 0;
  for (const [h, p] of r.live) {
    hands++;
    cbo += combosOf(h) * p;
  }
  const mixed = [...r.live.values()].filter((p) => p < 1).length;
  return (
    `Hero's range: ${hands} hands, ${r1(cbo)} combos` +
    (mixed ? ` (${mixed} held only part of the time)` : "") +
    ". Fold is the remainder inside it; everything else stays greyed out."
  );
}
